import { getNoteIndex } from './enharmonicUtils.js';
import { normalizeToV3, isChordString } from './chordModelMigration.js';
import { formatNote } from './noteFormatting.js';
import { parseChordString } from './chordParser.js';
// Major key degrees based on chromatic intervals (uppercase for major, lowercase for minor/diminished)
const MAJOR_ROMAN_NUMERALS = ["I", "♭II", "ii", "♭III", "iii", "IV", "♭V", "V", "♭VI", "vi", "♭VII", "vii°"];
// Minor key degrees based on natural minor scale intervals (lowercase for minor, uppercase for major)
const MINOR_ROMAN_NUMERALS = ["i", "♭II", "ii°", "III", "♭IV", "iv", "♭V", "v", "VI", "♭VII", "VII", "♭VIII"];
/**
 * Adjusts the case and symbols of a roman numeral based on chord quality.
 *
 * @param baseNumeral - The base roman numeral from the scale degree (e.g., "I", "ii", "vii°")
 * @param quality - The chord quality (e.g., "major", "minor", "diminished", "augmented")
 * @returns The adjusted roman numeral with correct case and symbols
 */
function adjustRomanNumeralForQuality(baseNumeral, quality) {
    if (!quality) {
        // No quality specified, return base numeral unchanged
        return baseNumeral;
    }
    // Remove any existing quality symbols (°, +) to get the base numeral
    const cleanNumeral = baseNumeral.replace(/[°+]/g, '');
    switch (quality) {
        case 'major':
            // Major chords use uppercase
            return cleanNumeral.toUpperCase();
        case 'minor':
        case 'minorMajor':
            // Minor chords use lowercase
            return cleanNumeral.toLowerCase();
        case 'diminished':
            // Diminished chords use lowercase with ° symbol
            return cleanNumeral.toLowerCase() + '°';
        case 'augmented':
            // Augmented chords use uppercase with + symbol
            return cleanNumeral.toUpperCase() + '+';
        case 'power':
        case 'sus4':
        case 'sus2':
            // Power and suspended chords are neither major nor minor, treat as uppercase
            return cleanNumeral.toUpperCase();
        default:
            // Unknown quality, return base numeral unchanged
            return baseNumeral;
    }
}
/**
 * Internal function that calculates the Roman numeral degree from a chord model.
 *
 * @param chord - Chord model in either V1 or V2 format
 * @param key - Key signature (string like "C" or "Am") or KeySignature object
 * @returns The Roman numeral string (e.g., "I", "♭II", "V") or null if inputs are invalid.
 */
function getChordDegreeFromModel(chord, key) {
    // Normalize to V3 for internal processing
    const v3 = normalizeToV3(chord);
    // Handle null root
    if (!v3.root) {
        return null;
    }
    const chordFundamental = formatNote(v3.root);
    // Parse key parameter
    let keyRootNote;
    let isMinorKey;
    if (typeof key === 'string') {
        if (!key) {
            return null;
        }
        // Determine if it's a minor key and extract the root note
        isMinorKey = key.endsWith('m');
        keyRootNote = isMinorKey ? key.slice(0, -1) : key;
    }
    else {
        // KeySignature object
        keyRootNote = formatNote(key.note);
        isMinorKey = key.mode === 'min';
    }
    const keyRootIndex = getNoteIndex(keyRootNote);
    const chordFundamentalIndex = getNoteIndex(chordFundamental);
    if (keyRootIndex === -1 || chordFundamentalIndex === -1) {
        return null;
    }
    let interval = chordFundamentalIndex - keyRootIndex;
    if (interval < 0) {
        interval += 12; // Ensure the interval is positive
    }
    // Use appropriate roman numeral array based on key type
    const romanNumerals = isMinorKey ? MINOR_ROMAN_NUMERALS : MAJOR_ROMAN_NUMERALS;
    if (interval >= 0 && interval < romanNumerals.length) {
        const baseNumeral = romanNumerals[interval];
        // Adjust the roman numeral based on the actual chord quality
        return adjustRomanNumeralForQuality(baseNumeral, v3.quality);
    }
    return null; // Should not happen with valid 0-11 interval
}
/**
 * Calculates the Roman numeral degree of a chord relative to a given key.
 *
 * Accepts both string chord names and chord models (V1/V2) for backward compatibility.
 *
 * @param chord - Chord name (string) or ChordObjectModel (V1/V2)
 * @param key - Key signature (string like "C" or "Am") or KeySignature object
 * @returns The Roman numeral string (e.g., "I", "♭II", "V") or null if inputs are invalid.
 *
 * @example
 * // String input (legacy API)
 * getChordDegree('C', 'C')  // Returns "I"
 * getChordDegree('Am', 'C')  // Returns "vi"
 *
 * @example
 * // V2 chord model input
 * const chord = { fundamental: { noteName: 'C', modifier: 'n' }, ... };
 * getChordDegree(chord, 'C')  // Returns "I"
 *
 * @example
 * // KeySignature object
 * const key = { note: { noteName: 'C', modifier: 'n' }, mode: 'maj' };
 * getChordDegree('G', key)  // Returns "V"
 */
export const getChordDegree = (chord, key) => {
    // Handle null/undefined inputs
    if (!chord || !key) {
        return null;
    }
    // Handle string input (legacy API)
    if (isChordString(chord)) {
        const parsedChord = parseChordString(chord);
        return getChordDegreeFromModel(parsedChord, key);
    }
    // Handle object input (V1 or V2)
    return getChordDegreeFromModel(chord, key);
};
/**
 * Calculates the Roman numeral degree for a slot relative to a given key root.
 * Custom chords (slots with customText) return null.
 * @param slot The slot containing the chord
 * @param keyRoot The root note of the key (e.g., "C", "G#", "Bb", "Am", "F#m").
 * @returns The Roman numeral string (e.g., "I", "♭II", "V") or null if slot has customText or invalid inputs.
 */
export const getSlotDegree = (slot, keyRoot) => {
    // Custom chords don't have degrees
    if (slot.customText) {
        return null;
    }
    // No key selected
    if (!keyRoot) {
        return null;
    }
    // No chord or no root
    if (!slot.chord) {
        return null;
    }
    const v3Chord = normalizeToV3(slot.chord);
    if (!v3Chord.root) {
        return null;
    }
    // Calculate degree using the existing function with the chord object
    return getChordDegree(v3Chord, keyRoot);
};
